'use client';

import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import useGlobalState from '@hooks/useGlobalState';
import DesktopHeaderLayoutContainer from '@components/pages/common/DesktopHeaderLayoutContainer';
import HeaderLayout from '@components/pages/common/HeaderLayoutContainer';
import { BiLogo } from '@wconcept/wconcept-common/models/renewal/popup';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';

import { PromotionPageServerProps } from '@models/promotion';
import { useScrollRestoration } from '@hooks/restoreScrollPosition';
import { useLoading } from '@contexts/LoadingContext';
import {
  useServerTime,
  useServerTimeActions,
  useServerTimeAutoRefresh,
  initializeServerTime,
} from '@store/serverTimeStore';
import useGnbController from '@hooks/useGnbController';
import { useAnchorScroll } from '@hooks/useAnchorScroll';
import { getPromotionContentIdsThroughOrder } from '@hooks/useVisibleModules';
import useLinkTo from '@hooks/useLinkTo';
import usePromotionVisibilityStore from '@store/promotionVisibilityStore';
import { isNativeAppBoth } from '@wconcept/wconcept-common/utils/userAgentUtil';
import GlobalToastPopup from '@components/common/GlobalToastPopup';
import usePromotionShare from '@hooks/promotion/usePromotionShare';
import { getPromotionShareImageUrl } from '@constants/promotionShare';
import useAirbridgeShareContext from '@hooks/useAirbridgeShareContext';
import {
  isInDisplayTimeRange,
  getDisplayStatus,
} from './utils/displayTimeChecker';
import PromotionModulesContainer from './PromotionModulesContainer';
import { PageManagedAnchorScrollProvider } from './components/ModuleAnchorWrapper';

const pageClassName = 'mega-promotion-page';
let loadingKeys = ['PROMOTION_MODULES'];

function PromotionPageContainer({
  gnbType,
  isMobile,
  isNativePage,
  adminPage,
}: {
  gnbType?: boolean;
  isMobile?: boolean;
  isNativePage?: boolean;
  adminPage?: boolean;
}) {
  // const searchParams = useSearchParams();
  const { initialData } = useGlobalState<PromotionPageServerProps>();
  const productListContent = initialData?.promotionPayload?.contents.find(
    content => content.moduleType === 'PRODUCT_LIST'
  );
  if (
    productListContent?.displayStatus === 'USE' &&
    productListContent.moduleType === 'PRODUCT_LIST' &&
    initialData?.initOrginalData?.total &&
    initialData?.initOrginalData?.total > 0
  ) {
    loadingKeys = ['PROMOTION_MODULES', 'PROMOTION_PRODUCTS'];
  }

  const { isAllLoaded } = useLoading(loadingKeys);
  const [anchorId, setAnchorId] = useState<number>(0);
  const [isPaused, setIsPaused] = useState<boolean>(false);

  // 동기적으로 URL 파라미터 가져오기
  const adminPreviewDate = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('previewDate') || '';
  }, []);
  const [isHtmlComplete, setHtmlComplete] = useState<boolean>(false);

  useEffect(() => {
    setAnchorId(
      parseInt(
        new URLSearchParams(window.location.search).get('anchorId') || '0',
        10
      )
    );
  }, []);
  // 초기 서버 시간 결정
  const initialServerTime = useMemo(() => {
    if (adminPreviewDate) {
      // 미리보기 날짜가 있으면 해당 날짜로 설정
      return adminPreviewDate;
    }
    return initialData?.serverTime;
  }, [adminPreviewDate, initialData?.serverTime]);

  // 서버 사이드에서도 초기화되도록 즉시 실행
  useEffect(() => {
    if (initialServerTime && typeof window !== 'undefined') {
      initializeServerTime(initialServerTime);
    }
  }, [initialServerTime]);

  const { fetchServerTime } = useServerTimeActions({
    initialServerTime,
  });

  // 스토어에서 queryStatusMap 가져오기
  const accumulatedQueryStatus = usePromotionVisibilityStore(
    state => state.accumulatedQueryStatus
  );

  // 추천상품 쿼리의 로딩 상태 확인
  const isRecommendFetching = useIsFetching({
    queryKey: ['promotionProductsRecommend'],
  });

  // 앵커의 Y 좌표는 앞선 모듈 높이에 영향을 받으므로 대상까지의 상품 요청을 추적한다.
  const targetContentIds = useMemo(
    () =>
      getPromotionContentIdsThroughOrder(
        initialData?.promotionPayload?.contents ?? [],
        anchorId
      ),
    [anchorId, initialData?.promotionPayload?.contents]
  );
  const isTargetProductFetching = useIsFetching({
    predicate: query =>
      query.queryKey.some(key => targetContentIds.has(String(key))),
  });

  const isTargetModuleLoading = useMemo(() => {
    if (!anchorId || targetContentIds.size === 0) return false;

    const isLoadingAny = Array.from(targetContentIds).some(contentId => {
      const queryStatus = accumulatedQueryStatus.get(contentId);
      return queryStatus?.isLoading || queryStatus?.isFetching || false;
    });

    return (
      isLoadingAny || isRecommendFetching > 0 || isTargetProductFetching > 0
    );
  }, [
    anchorId,
    targetContentIds,
    accumulatedQueryStatus,
    isRecommendFetching,
    isTargetProductFetching,
  ]);

  // 일반 프로모션은 페이지에서 로딩을 기다린 뒤 앵커 이동을 한 번만 처리한다.
  useAnchorScroll({
    order: anchorId,
    waitForLoading: true,
    isLoading: !isHtmlComplete || isTargetModuleLoading,
    delay: 200,
    maxWaitTime: 8000,
    isDesktop: !isMobile,
  });
  const { linkTo } = useLinkTo();

  useScrollRestoration(isAllLoaded && isHtmlComplete);

  const { gnbInfo, marketingPopup, megaMenu, searchInitInfo } =
    useGnbController({
      initialGnbInfo: initialData?.gnbInfo,
      initialMarketingPopup: initialData?.logoInfo,
      initialMegaMenu: initialData?.megaMenu,
      initialSearchInitInfo: initialData?.searchInitInfo,
    });

  // 초기 서버타임 조회 및 자동 갱신 설정
  useEffect(() => {
    // 어드민 페이지에서 미리보기 날짜가 있으면 서버 시간 조회하지 않음
    if (!adminPage && !adminPreviewDate) {
      fetchServerTime();
    }
  }, [adminPage, adminPreviewDate, fetchServerTime]);

  const onRedirectToHome = useCallback(() => {
    if (isNativeAppBoth(window.navigator.userAgent)) {
      window.location.href = 'wconceptkr://actionBarHome';
    } else {
      linkTo.push({
        url: '/rn/women',
      });
    }
  }, [linkTo]);

  const promotion = initialData?.promotionPayload?.promotion;
  useAirbridgeShareContext({
    type: 'promotion',
    imageUrl:
      promotion?.ogImagePath ||
      getPromotionShareImageUrl(promotion?.promotionId),
    profileName: promotion?.title,
  });
  const {
    renderDesktopShareButtons,
    renderMobileShareButtons,
    openMobileShareModal,
  } = usePromotionShare({
    title: promotion?.title ?? '',
    promotionId: promotion?.promotionId,
    imageUrl: promotion?.ogImagePath,
  });
  useEffect(() => {
    if (adminPage) return;

    const displayStatus = getDisplayStatus(
      initialData?.promotionPayload?.promotion?.displayStartDate,
      initialData?.promotionPayload?.promotion?.displayEndDate
    );

    if (displayStatus === 'before') {
      alert('프로모션을 준비 중입니다');
      onRedirectToHome();
    } else if (displayStatus === 'after') {
      alert('종료된 프로모션입니다');
      onRedirectToHome();
    }
  }, []);

  if (
    !adminPage &&
    !isInDisplayTimeRange(
      initialData?.promotionPayload?.promotion?.displayStartDate,
      initialData?.promotionPayload?.promotion?.displayEndDate
    )
  ) {
    return null;
  }

  return (
    <>
      {!isMobile ? (
        <DesktopHeaderLayoutContainer
          className={pageClassName}
          contTitsuffix={renderDesktopShareButtons()}
          pageTitle={initialData?.promotionPayload?.promotion?.title}
          megaMenu={megaMenu}
          gnbInfo={gnbInfo || []}
          searchInitInfo={searchInitInfo}
        >
          <PageManagedAnchorScrollProvider>
            <PromotionModulesContainer
              promotionInfo={initialData?.promotionPayload}
              isMobile={!!isMobile}
              nonce={initialData?.nonce}
              serverTime={initialServerTime}
              productListTotalCount={initialData?.initOrginalData?.total}
              isPaused={isPaused}
              setIsPaused={setIsPaused}
              setHtmlComplete={setHtmlComplete}
              initialVisibleThroughOrder={anchorId}
            />
          </PageManagedAnchorScrollProvider>
        </DesktopHeaderLayoutContainer>
      ) : (
        <HeaderLayout
          className={pageClassName}
          isFooter={false}
          subHeaderType={gnbType ? undefined : 'subDetail'}
          gnbType={gnbType ? 'Y' : 'N'}
          isShareBtn
          onClickShare={openMobileShareModal}
          logoInfo={marketingPopup?.biLogo as BiLogo}
          gnbInfo={gnbInfo}
          pageTitle={initialData?.promotionPayload?.promotion?.title}
          selectedGnbValue={`/rn/promotion/${initialData?.promotionPayload?.promotion.promotionId}`}
          isProfileBtn={false}
        >
          <PageManagedAnchorScrollProvider>
            <PromotionModulesContainer
              promotionInfo={initialData?.promotionPayload}
              isMobile={!!isMobile}
              nonce={initialData?.nonce}
              gnbType={gnbType}
              serverTime={initialServerTime}
              productListTotalCount={initialData?.initOrginalData?.total}
              isPaused={isPaused}
              setIsPaused={setIsPaused}
              setHtmlComplete={setHtmlComplete}
              initialVisibleThroughOrder={anchorId}
              renderMobileShareButton={
                gnbType ? undefined : renderMobileShareButtons
              }
            />
          </PageManagedAnchorScrollProvider>
        </HeaderLayout>
      )}
      <GlobalToastPopup />
    </>
  );
}

export default memo(PromotionPageContainer);
